import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useNotifications } from '../hooks/useNotifications';
import { toast } from "@/hooks/use-toast";
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
}

interface TrainingNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: TrainingSession | null;
}

const TrainingNotificationDialog: React.FC<TrainingNotificationDialogProps> = ({
  open,
  onOpenChange,
  session
}) => {
  useUiLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { sendNotification } = useNotifications();
  const { impersonation, profile } = useAuth();

  const handleSendNotification = async () => {
    if (!session) return;

    // Get the dojo_id from impersonation context or current profile
    const dojoId = impersonation.active ? impersonation.dojoId : profile?.dojo_id;

    if (!dojoId) {
      console.error('No dojo context found');
      return;
    }

    setIsLoading(true);

    try {
      // Use the session's age group or default to 'all'
      let ageGroup: 'all' | 'kids' | 'teens' | 'adults' = 'all';

      if (session.age_group === 'kids') {
        ageGroup = 'kids';
      } else if (session.age_group === 'teens') {
        ageGroup = 'teens';
      } else if (session.age_group === 'adults') {
        ageGroup = 'adults';
      }

      // Get students based on session's age group from the correct dojo
      let studentsQuery = supabase
        .from('profiles')
        .select('id, email, first_name, last_name, age_group')
        .eq('role', 'student')
        .eq('dojo_id', dojoId);

      // Apply age filtering based on session's age group
      if (ageGroup === 'kids') {
        studentsQuery = studentsQuery.eq('age_group', 'kids');
      } else if (ageGroup === 'teens') {
        studentsQuery = studentsQuery.eq('age_group', 'teens');
      } else if (ageGroup === 'adults') {
        studentsQuery = studentsQuery.eq('age_group', 'adults');
      }

      const { data: students, error: studentsError } = await studentsQuery;

      if (studentsError) {
        throw studentsError;
      }

      if (!students || students.length === 0) {
        const ageGroupText = ageGroup === 'kids' ? uiText("Kids (5-12)") : ageGroup === 'teens' ? uiText("Teens (13-17)") : ageGroup === 'adults' ? uiText("Adults (18+)") : 'selected';
        toast({
          title: uiText("Notice"),
          description: uiText("No students found in the {{value0}} age group.", { value0: ageGroupText })
        });
        return;
      }

      // Create notifications in the database for filtered students
      const notifications = students.map(student => ({
        user_id: student.id,
        type: 'training_session',
        title: uiText("New Training Session: {{value0}}", { value0: session.title }),
        message: uiText("Training session scheduled for {{value0}} at {{value1}}", { value0: new Date(session.date).toLocaleDateString(currentLocale()), value1: session.start_time }),
        session_id: session.id
      }));

      const { error: notificationError } = await supabase
        .from('notifications')
        .insert(notifications);

      if (notificationError) {
        console.error('Error creating notifications:', notificationError);
        throw notificationError;
      }

      // Send email notifications using the edge function
      const success = await sendNotification({
        type: 'training_session',
        title: session.title,
        message: uiText("Training session scheduled for {{value0}} at {{value1}}", { value0: new Date(session.date).toLocaleDateString(currentLocale()), value1: session.start_time }),
        dojoId: dojoId,
        sessionData: {
          date: session.date,
          start_time: session.start_time,
          end_time: session.end_time,
          description: session.description
        },
        ageFilter: ageGroup
      });

      if (success) {
        const ageGroupText = ageGroup === 'kids' ? uiText(" (Kids 5-12)") : ageGroup === 'teens' ? uiText(" (Teens 13-17)") : ageGroup === 'adults' ? uiText(" (Adults 18+)") : '';
        toast({
          title: uiText("Training Notification Sent"),
          description: uiText("Training session notification has been sent to students{{value0}}!", { value0: ageGroupText })
        });

        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error sending training notification:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to send training notification. Please try again."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{uiText("Send Training Session Notification")}</DialogTitle>
          <DialogDescription>
            {uiText("Send a notification to students about this training session")}</DialogDescription>
        </DialogHeader>

        {session && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold">{session.title}</h4>
              <p className="text-sm text-gray-600">
                {new Date(session.date).toLocaleDateString(currentLocale())} {' '}{uiText("at")}{' '}{session.start_time} - {session.end_time}
              </p>
              {session.description && (
                <p className="text-sm text-gray-600 mt-2">{session.description}</p>
              )}
              <p className="text-sm text-blue-600 mt-2">
                {uiText("Target:")}{' '}{session.age_group === 'kids' ? uiText("Kids (5-12)") : session.age_group === 'teens' ? uiText("Teens (13-17)") : session.age_group === 'adults' ? uiText("Adults (18+)") : uiText("All Ages")}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {uiText("Cancel")}</Button>
          <Button
            onClick={handleSendNotification}
            disabled={isLoading || !session}
            className="bg-red-900 hover:bg-red-800"
          >
            {isLoading ? uiText("Sending...") : uiText("Send Notification")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrainingNotificationDialog;